import {
  AppError,
  Loader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
} from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import type { TFunction } from "i18next";
import { useDeliverApi } from "@bfc-software/bfc-web-ui/lib/deliver-api-client";

import { formatCount } from "../-data/format";
import { jsonBody } from "../-data/json-body";

import { ScanBar } from "./scan-bar";
import styles from "./route-stops.module.css";

type Props = {
  warehouse: number;
  routeId: string;
  lang: string;
  t: TFunction;
};

/** The service's own ceiling; one route's stops fit in a single page. */
const STOPS_SIZE = 500;

/**
 * What a routes-table row opens into: that route's stops, in a table of their
 * own. Fetched when the row expands, not before, so a screen of collapsed rows
 * costs nothing.
 */
export function RouteStops({ warehouse, routeId, lang, t }: Props) {
  const client = useDeliverApi();
  const query = client.useQuery(
    "get",
    "/app/deliver/api/routes/{routeId}/stops",
    {
      params: {
        path: { routeId },
        query: { warehouse, page: 0, size: STOPS_SIZE },
      },
    },
  );
  const rows = jsonBody(query.data)?.rows ?? [];
  const none = t("none");
  const n = (value: number) => formatCount(value, lang);

  if (query.isLoading) {
    return (
      <div className={styles.state} data-test-role="route-stops-loading">
        <Loader type="inline" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className={styles.state} data-test-role="route-stops-failed">
        <AppError
          description={t("stops.failed")}
          resolution={t("list.failureSupport")}
        />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className={styles.state} data-test-role="route-stops-empty">
        {t("stops.empty")}
      </p>
    );
  }

  return (
    <div className={styles.stops} data-test-role="route-stops">
      <Table
        aria-label={t("stops.ariaLabel", { route: routeId })}
        isResizable={false}
      >
        <TableHead>
          <TableHeaderCell isRowHeader={true}>
            {t("stops.stop")}
          </TableHeaderCell>
          <TableHeaderCell>{t("stops.customer")}</TableHeaderCell>
          <TableHeaderCell>{t("stops.status")}</TableHeaderCell>
          <TableHeaderCell>{t("stops.started")}</TableHeaderCell>
          <TableHeaderCell>{t("stops.finished")}</TableHeaderCell>
          <TableHeaderCell>{t("stops.delivered")}</TableHeaderCell>
          <TableHeaderCell>{t("stops.scanPct")}</TableHeaderCell>
          <TableHeaderCell>{t("stops.returns")}</TableHeaderCell>
          <TableHeaderCell>{t("stops.signature")}</TableHeaderCell>
        </TableHead>
        <TableBody>
          {rows.map((stop) => {
            return (
              <TableRow key={stop.stop} data-test-role="route-stop-row">
                <TableCell>{stop.stop}</TableCell>
                <TableCell>{stop.customerName || none}</TableCell>
                <TableCell>
                  {stop.status ? (
                    <Tag
                      label={stop.status}
                      display={{
                        tone: /complet|deliver/i.test(stop.status)
                          ? "success"
                          : "info",
                      }}
                    />
                  ) : (
                    none
                  )}
                </TableCell>
                <TableCell>
                  <span className={styles.nowrap}>
                    {stop.startTimestamp ?? none}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={styles.nowrap}>
                    {stop.finishTimestamp ?? none}
                  </span>
                </TableCell>
                <TableCell>{n(stop.piecesDelivered)}</TableCell>
                <TableCell>
                  <ScanBar value={stop.scanPct} lang={lang} none={none} />
                </TableCell>
                <TableCell>{n(stop.currentReturns)}</TableCell>
                <TableCell>{stop.signatureName || none}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
